import styled from 'foundations-theming/utils';
import { Column, InfiniteLoader, WindowScroller, defaultTableRowRenderer } from 'react-virtualized';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UISearchInput from 'UIComponents/input/UISearchInput';
import { getFormattedMessageWithObjectTypeName } from 'automation-ui-i18n/i18nUtils';
import EmptyObjectSearchState from 'automation-ui-enrollment/components/filters/enrollment/generic-enrollment-table/EmptyObjectSearchState';
import { TABLE_METADATA_BY_OBJECT, hasRowConfiguration } from 'automation-ui-enrollment/components/enrollment/manual/ObjectTableMetadata';
import PropertyLabel from 'automation-ui-enrollment/components/enrollment/manual/PropertyLabel';
import StyledReactVirtualizedTable from 'automation-ui-enrollment/components/common/StyledReactVirtualizedTable';
import { TableCheckboxCell, TableCustomObjectDataCell, TableObjectDataCell, TableObjectNameCell } from 'automation-ui-enrollment/components/enrollment/manual/PreviewEnrollCellRenderers';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledEnrollPanelContainer = styled.div.withConfig({
  displayName: "PreviewEnrollTable__StyledEnrollPanelContainer",
  componentId: "sc-8of53o-0"
})(["max-width:550px;margin:auto;"]);
const StyledSearchContainer = styled.div.withConfig({
  displayName: "PreviewEnrollTable__StyledSearchContainer",
  componentId: "sc-8of53o-1"
})(["position:sticky;top:0;z-index:2;background:", ";border-bottom:1px solid ", ";"], ({
  theme
}) => theme.color['do-not-use-olaf'], ({
  theme
}) => theme.color['do-not-use-great-white']);
const CELL_HEIGHT = 50;
const CHECKBOX_WIDTH = 50;
const FETCH_MORE_COUNT = 100;
const PreviewEnrollTable = ({
  allObjectProperties,
  objectTypeId,
  tableWidth,
  selectable,
  checkedItems,
  objectTypeDefinition,
  onSelectObject,
  onLoadMore,
  scrollContainer,
  searchQuery,
  handleSearchQueryChange,
  isMultiSelect = true
}) => {
  const rowConfig = TABLE_METADATA_BY_OBJECT[objectTypeId];
  // in the custom object case the row config is undefined and we calculate the number of columns by the display properties size
  const numColumns = rowConfig && rowConfig.numColumns || 1 + objectTypeDefinition.secondaryDisplayLabelPropertyNames.length;
  const cellWidth = (selectable && isMultiSelect ? tableWidth - CHECKBOX_WIDTH : tableWidth) / numColumns;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(StyledSearchContainer, {
      className: "p-x-10 p-bottom-5 p-top-5",
      children: /*#__PURE__*/_jsx(UISearchInput, {
        value: searchQuery,
        onChange: handleSearchQueryChange,
        placeholder: getFormattedMessageWithObjectTypeName({
          baseLangKey: 'automation-ui-enrollment.generic-enrollment-table.search_cob',
          objectTypeId,
          objectTypeDefinition: objectTypeDefinition
        })
      })
    }), allObjectProperties && allObjectProperties.results && allObjectProperties.total > 0 && /*#__PURE__*/_jsx(InfiniteLoader, {
      isRowLoaded: row => {
        return !!allObjectProperties.results[row.index];
      },
      loadMoreRows: ({
        startIndex
      }) => {
        onLoadMore({
          variables: {
            offset: startIndex,
            count: FETCH_MORE_COUNT
          }
        });
        return Promise.resolve();
      },
      rowCount: allObjectProperties.total,
      children: ({
        onRowsRendered,
        registerChild
      }) => /*#__PURE__*/_jsx(WindowScroller, {
        scrollElement: scrollContainer,
        children: ({
          scrollTop,
          height
        }) => /*#__PURE__*/_jsxs(StyledReactVirtualizedTable, {
          rowRenderer: defaultTableRowRenderer,
          autoHeight: true,
          width: tableWidth,
          height: height || 0,
          ref: registerChild,
          onRowsRendered: onRowsRendered,
          headerHeight: CELL_HEIGHT,
          rowHeight: CELL_HEIGHT,
          rowCount: allObjectProperties.results.length
          // react-virtualized table required to have rowGetter function
          ,
          rowGetter: () => ({}),
          scrollTop: scrollTop,
          children: [selectable && /*#__PURE__*/_jsx(Column, {
            dataKey: "selected",
            headerClassName: "objects-table-header",
            className: "objects-table-row",
            cellRenderer: row => /*#__PURE__*/_jsx(TableCheckboxCell, {
              rowIndex: row.rowIndex,
              allObjectPropertiesResults: allObjectProperties.results,
              checkedItems: checkedItems,
              onSelectObject: onSelectObject,
              isMultiSelect: isMultiSelect
            }),
            width: CHECKBOX_WIDTH
          }), /*#__PURE__*/_jsx(Column, {
            dataKey: "objectName",
            headerClassName: "objects-table-header",
            className: "objects-table-row",
            cellRenderer: row => /*#__PURE__*/_jsx(TableObjectNameCell, {
              rowIndex: row.rowIndex,
              rowConfig: rowConfig,
              objectTypeId: objectTypeId,
              allObjectPropertiesResults: allObjectProperties.results
            }),
            headerRenderer: () => {
              if (!hasRowConfiguration(objectTypeId)) {
                return /*#__PURE__*/_jsx(PropertyLabel, {
                  property: objectTypeDefinition.primaryDisplayLabelPropertyName,
                  objectTypeId: objectTypeId
                });
              } else {
                return /*#__PURE__*/_jsx(FormattedMessage, {
                  message: rowConfig.labels[0]
                });
              }
            },
            width: cellWidth
          }), !hasRowConfiguration(objectTypeId) && objectTypeDefinition.secondaryDisplayLabelPropertyNames.map(property => /*#__PURE__*/_jsx(Column, {
            dataKey: "customObjectData",
            headerClassName: "objects-table-header",
            className: "objects-table-row",
            cellRenderer: row => /*#__PURE__*/_jsx(TableCustomObjectDataCell, {
              rowIndex: row.rowIndex,
              property: property,
              allObjectPropertiesResults: allObjectProperties.results
            }),
            headerRenderer: () => {
              return /*#__PURE__*/_jsx(PropertyLabel, {
                property: property,
                objectTypeId: objectTypeId
              });
            },
            width: cellWidth
          }, property)), hasRowConfiguration(objectTypeId) && rowConfig.rowLabelCreators.map((rowLabelCreator, index) => /*#__PURE__*/_jsx(Column, {
            dataKey: "objectData",
            headerClassName: "objects-table-header",
            className: "objects-table-row",
            cellRenderer: row => /*#__PURE__*/_jsx(TableObjectDataCell, {
              rowIndex: row.rowIndex,
              rowLabelCreator: rowLabelCreator,
              allObjectPropertiesResults: allObjectProperties.results
            }),
            headerRenderer: () => /*#__PURE__*/_jsx(FormattedMessage, {
              message: rowConfig.labels[index + 1]
            }),
            width: cellWidth
          }, index))]
        })
      })
    }), allObjectProperties && allObjectProperties.total === 0 && /*#__PURE__*/_jsx(StyledEnrollPanelContainer, {
      children: /*#__PURE__*/_jsx(EmptyObjectSearchState, {
        objectTypeId: objectTypeId
      })
    })]
  });
};
export default PreviewEnrollTable;